import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

/**
 * Custom hook for managing inquiry lists
 * Provides CRUD operations and real-time updates for user's inquiry lists
 */
export const useInquiryLists = (userId) => {
  const [inquiryLists, setInquiryLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all inquiry lists for the user
  const fetchInquiryLists = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .rpc('get_user_inquiry_lists', { user_uuid: userId });

      if (fetchError) throw fetchError;

      setInquiryLists(data || []);
    } catch (err) {
      console.error('Error fetching inquiry lists:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Create a new inquiry list
  const createList = useCallback(async (listName, description = null) => {
    if (!userId) throw new Error('User ID is required');

    try {
      const { data, error: createError } = await supabase
        .rpc('create_inquiry_list', {
          user_uuid: userId,
          list_name: listName,
          description: description
        });

      if (createError) throw createError;

      // Refresh the list
      await fetchInquiryLists();

      return data;
    } catch (err) {
      console.error('Error creating inquiry list:', err);
      throw err;
    }
  }, [userId, fetchInquiryLists]);

  // Update an inquiry list
  const updateList = useCallback(async (listId, updates) => {
    try {
      const { error: updateError } = await supabase
        .from('inquiry_lists')
        .update({
          list_name: updates.listName,
          description: updates.description,
          updated_at: new Date().toISOString()
        })
        .eq('id', listId)
        .eq('user_id', userId);

      if (updateError) throw updateError;

      // Refresh the list
      await fetchInquiryLists();
    } catch (err) {
      console.error('Error updating inquiry list:', err);
      throw err;
    }
  }, [userId, fetchInquiryLists]);

  // Delete an inquiry list
  const deleteList = useCallback(async (listId) => {
    try {
      const { error: deleteError } = await supabase
        .from('inquiry_lists')
        .delete()
        .eq('id', listId)
        .eq('user_id', userId);

      if (deleteError) throw deleteError;

      // Refresh the list
      await fetchInquiryLists();
    } catch (err) {
      console.error('Error deleting inquiry list:', err);
      throw err;
    }
  }, [userId, fetchInquiryLists]);

  // Submit inquiry for review
  const submitInquiry = useCallback(async (listId) => {
    try {
      const { data, error: submitError } = await supabase
        .rpc('submit_inquiry', {
          inquiry_list_id: listId,
          user_uuid: userId
        });

      if (submitError) throw submitError;

      // Refresh the list
      await fetchInquiryLists();

      return data;
    } catch (err) {
      console.error('Error submitting inquiry:', err);
      throw err;
    }
  }, [userId, fetchInquiryLists]);

  // Initial fetch
  useEffect(() => {
    fetchInquiryLists();
  }, [fetchInquiryLists]);

  // Set up real-time subscription
  useEffect(() => {
    if (!userId) return;

    const channel = supabase
      .channel('inquiry_lists_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'inquiry_lists',
          filter: `user_id=eq.${userId}`
        },
        () => {
          // Refresh the list when changes occur
          fetchInquiryLists();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, fetchInquiryLists]);

  return {
    inquiryLists,
    loading,
    error,
    createList,
    updateList,
    deleteList,
    submitInquiry,
    refreshLists: fetchInquiryLists
  };
};

/**
 * Custom hook for managing items within a specific inquiry list
 * Provides CRUD operations and real-time updates for inquiry items
 */
export const useInquiryItems = (inquiryListId, userId) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all items for the inquiry list
  const fetchItems = useCallback(async () => {
    if (!inquiryListId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('inquiry_items')
        .select('*')
        .eq('inquiry_list_id', inquiryListId)
        .order('sort_order', { ascending: true });

      if (fetchError) throw fetchError;

      setItems(data || []);
    } catch (err) {
      console.error('Error fetching inquiry items:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [inquiryListId]);

  // Add an item to the inquiry list
  const addItem = useCallback(async (partNumber, description, quantity = 1, notes = null) => {
    try {
      const { data, error: addError } = await supabase
        .rpc('add_inquiry_item', {
          inquiry_list_id: inquiryListId,
          part_number: partNumber,
          description: description,
          quantity: quantity,
          notes: notes
        });

      if (addError) throw addError;

      // Refresh items
      await fetchItems();

      return data;
    } catch (err) {
      console.error('Error adding inquiry item:', err);
      throw err;
    }
  }, [inquiryListId, fetchItems]);

  // Update an item
  const updateItem = useCallback(async (itemId, updates) => {
    try {
      const { error: updateError } = await supabase
        .from('inquiry_items')
        .update(updates)
        .eq('id', itemId)
        .eq('inquiry_list_id', inquiryListId);

      if (updateError) throw updateError;

      // Refresh items
      await fetchItems();
    } catch (err) {
      console.error('Error updating inquiry item:', err);
      throw err;
    }
  }, [inquiryListId, fetchItems]);

  // Remove an item
  const removeItem = useCallback(async (itemId) => {
    if (!userId) throw new Error('User ID is required');

    try {
      const { data, error: removeError } = await supabase
        .rpc('remove_inquiry_item', {
          item_id: itemId,
          user_uuid: userId
        });

      if (removeError) throw removeError;

      // Refresh items
      await fetchItems();

      return data;
    } catch (err) {
      console.error('Error removing inquiry item:', err);
      throw err;
    }
  }, [userId, fetchItems]);

  // Initial fetch
  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Set up real-time subscription
  useEffect(() => {
    if (!inquiryListId) return;

    const channel = supabase
      .channel('inquiry_items_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'inquiry_items',
          filter: `inquiry_list_id=eq.${inquiryListId}`
        },
        () => {
          // Refresh items when changes occur
          fetchItems();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [inquiryListId, fetchItems]);

  return {
    items,
    loading,
    error,
    addItem,
    updateItem,
    removeItem,
    refreshItems: fetchItems
  };
};
